import React, { useState, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';

import { withStyles } from '@material-ui/core/styles';

import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableSortLabel from '@material-ui/core/TableSortLabel';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTablePagination from '@material-ui/core/TablePagination';
import MuiBox from '@material-ui/core/Box';
import MuiTableFooter from '@material-ui/core/TableFooter';
import MuiCheckbox from '@material-ui/core/Checkbox';
import ColumnFilter from 'components/ColumnFilter';
import ActiveFilters from 'components/ActiveFilters';
import BulkActionsRoyaltyTransfers from 'components/BulkActionsRoyaltyTransfers';
import AlertToast from 'components/AlertToast';
import useColumnFilters from 'hooks/use-column-filters';
import useTableSelection from 'hooks/use-table-selection';

import * as API from 'api';

import Row from './row';

const STALE_QUERY_TIME = 20 * 1000;

const {
  unwindRoyalyTransfers,
  getAll: getAllRoyaltyTransfer,
  close: closeRoyaltyTransfer,
  patch: patchRoyaltyTransfer,
  executeExternalTransfer,
  getRoyaltyTransfer,
  exportTransferCsv,
  refreshTransferAmount,
} = API.RoyaltyTransfers();

const styles = () => ({
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

const createRowData = row => {
  // Determine transfer state based on backend logic
  let transfer_state = '';
  const amount = parseFloat(row.expected_transfer_amount || 0);

  if (row.transferred_at) {
    transfer_state = 'Closed, transferred';
  } else if (row.is_closed) {
    transfer_state = 'Closed, not transferred';
  } else if (amount <= 0 || row.expected_transfer_amount === null) {
    transfer_state = 'Zero/Null/Loading Amount';
  } else if (amount > 50) {
    transfer_state = 'Open, above minthreshold';
  } else if (amount > 0) {
    transfer_state = 'Open, below minthreshold';
  }

  return {
    id: row.id,
    block_bulk_operations: row.block_bulk_operations,
    payment_account_id: row.payment_account?.id,
    user_group_type: row?.payment_account?.user ? 'Person' : 'Label',
    user_group_name: row?.payment_account?.user ? row?.payment_account?.user?.name : row?.payment_account?.group?.name,
    account_email: row?.payment_account?.user ? row?.payment_account?.user?.email : row?.payment_account?.group?.email,
    stripe_account_id: row.payment_account?.stripe_account_id,
    expected_transfer_amount: row.expected_transfer_amount,
    external_transfer_id: row.external_transfer_id,
    transferred_amount: row.transferred_amount,
    untransferred_amount: row.untransferred_amount,
    can_close: row.can_close,
    is_closed: row.is_closed,
    closed_at: row.closed_at,
    can_execute_external_transfer: row.can_execute_external_transfer,
    transferred_at: row.transferred_at,
    external_transfer_method: row.external_transfer_method,
    deducted_fee_amount: row.deducted_fee_amount,
    transfer_state,
  };
};

const TableRoyaltyTransfers = props => {
  const { classes, onEditRow, refetchCompanyStripeBalanceUSD, refetchTotalPayable } = props;

  const [editingId, setEditingId] = useState(null);
  const [isClosing, setIsClosing] = useState([]);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const [params, setParams] = useState({
    page: 0,
    rowsPerPage: 250,
    order: 'desc',
    orderBy: 'royalty_transfer_id',
  });

  // Column filters configuration
  const columnFiltersConfig = useMemo(
    () => [
      {
        id: 'royalty_transfer_id',
        label: 'ID',
        filterType: 'text',
        apiParam: 'royalty_transfer_id',
      },
      {
        id: 'user_group_type',
        label: 'Type',
        filterType: 'select',
        apiParam: 'user_group_type',
        options: [
          { value: 'Person', label: 'Person' },
          { value: 'Label', label: 'Label' },
        ],
      },
      {
        id: 'account_info',
        label: 'Account',
        filterType: 'text',
        apiParam: 'account_info',
      },
      {
        id: 'external_transfer_method',
        label: 'Method',
        filterType: 'select',
        apiParam: 'external_transfer_method',
        options: [], // Will be populated dynamically
      },
      {
        id: 'status',
        label: 'Status',
        filterType: 'select',
        apiParam: 'state',
        options: [
          { value: 'Open, above minthreshold', label: 'Open, above min threshold ($50+)' },
          { value: 'Open, below minthreshold', label: 'Open, below min threshold (<$50)' },
          { value: 'Closed, not transferred', label: 'Closed, not transferred' },
          { value: 'Closed, transferred', label: 'Closed, transferred' },
          { value: 'Zero/Null/Loading Amount', label: 'Zero/Null/Loading Amount' },
          { value: 'All', label: 'All' },
        ],
      },
    ],
    []
  );

  // Toast functions - defined early to be used in hooks
  const showToast = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
  }, []);

  const clearToast = useCallback(() => {
    setToastMessage('');
  }, []);

  // Filter hooks
  const {
    filters,
    filterParams,
    openFilterColumn,
    tempFilters,
    openFilterPopup,
    updateTempFilter,
    applyFilter,
    clearCurrentFilter,
    clearAllFilters,
    hasColumnFilter,
    removeFilter,
  } = useColumnFilters(columnFiltersConfig, 'royalty-transfers', showToast);

  // Combine filter params with existing params
  const combinedParams = useMemo(() => {
    return {
      ...params,
      ...filterParams,
    };
  }, [params, filterParams]);

  const {
    data: royaltyTransfers,
    isFetching: isFetchingRoyaltyTransfers,
    refetch,
  } = useQuery(
    ['allRoyaltyTransfers', combinedParams],
    () => getAllRoyaltyTransfer({ params: { ...combinedParams } }),
    {
      refetchOnWindowFocus: false,
      staleTime: STALE_QUERY_TIME,
    }
  );

  // Dynamic method options based on data
  const methodOptions = useMemo(() => {
    if (!royaltyTransfers?.results) return [];

    const uniqueMethods = [
      ...new Set(
        royaltyTransfers.results
          .map(row => row.external_transfer_method)
          .filter(method => method && method.trim() !== '')
      ),
    ];

    return uniqueMethods.map(method => ({
      value: method,
      label: method,
    }));
  }, [royaltyTransfers]);

  // Update method options in filter config
  const updatedColumnFiltersConfig = useMemo(() => {
    return columnFiltersConfig.map(config => {
      if (config.id === 'external_transfer_method') {
        return { ...config, options: methodOptions };
      }
      return config;
    });
  }, [columnFiltersConfig, methodOptions]);

  const refreshPageQueries = useCallback(() => {
    refetchCompanyStripeBalanceUSD();
    refetchTotalPayable();
    refetch();
  }, [refetchCompanyStripeBalanceUSD, refetchTotalPayable, refetch]);

  function handleChangePage(_event, newPage) {
    setParams({ ...params, page: newPage });
  }

  function handleChangeRowsPerPage({ target: { value: newRowsPerPage } }) {
    setParams({ ...params, page: 0, rowsPerPage: newRowsPerPage });
  }

  const unwindRoyaltyMutation = useMutation(({ id }) => unwindRoyalyTransfers(id), {
    onSuccess: () => {
      showToast('Transfer unwound successfully');
      refreshPageQueries();
    },
    onError: error => {
      showToast(error?.data?.detail || 'Failed to unwind transfer', 'error');
      unwindRoyaltyMutation.reset();
    },
  });

  const closeRoyaltyTransferMutation = useMutation(({ id }) => closeRoyaltyTransfer(id), {
    onSuccess: data => {
      const {
        job: {
          data: { royaltyTransferId: id },
        },
      } = data;

      let attempts = 0;
      const attemptInterval = setInterval(async () => {
        const updated = await getRoyaltyTransfer(id);

        if (attempts > 5) {
          finish();
          showToast(
            `Closing transfer ${id} is taking longer than expected. Please refresh the page in a moment.`,
            'warning'
          );
        }
        attempts++;
        if (updated.is_closed) {
          finish();
        }
      }, 5000);

      async function finish() {
        clearInterval(attemptInterval);
        await refetch();
        setIsClosing(() => {
          return [isClosing.filter(i => i !== id)];
        });
      }
    },
    onError: error => {
      showToast(error?.data?.detail || 'Failed to close transfer', 'error');
      closeRoyaltyTransferMutation.reset();
    },
  });

  const executeRefreshMutation = useMutation(({ id }) => refreshTransferAmount(id), {
    onSuccess: () => {
      setEditingId(null);
      refreshPageQueries();
    },
    onError: error => {
      showToast(error?.data?.detail || 'Failed to refresh transfer amount', 'error');
      executeRefreshMutation.reset();
    },
  });

  const executeExternalTransferMutation = useMutation(({ id }) => executeExternalTransfer(id), {
    onSuccess: () => {
      setEditingId(null);
      refreshPageQueries();
    },
    onError: error => {
      showToast(error?.data?.detail || 'Failed to execute transfer', 'error');
      executeExternalTransferMutation.reset();
    },
  });

  const executePatchMutation = useMutation(({ id, patchObject }) => patchRoyaltyTransfer(id, patchObject), {
    onSuccess: () => {
      setEditingId(null);
      refreshPageQueries();
    },
    onError: error => {
      showToast(error?.data?.detail || 'Failed to update transfer', 'error');
      executePatchMutation.reset();
    },
  });

  const headCells = [
    {
      id: 'select',
      numeric: false,
      label: '',
      width: '50px',
      align: 'center',
    },
    {
      id: 'royalty_transfer_id',
      numeric: true,
      label: 'Id',
      sortable: true,
      align: 'center',
    },
    {
      id: 'user_group_type',
      numeric: false,
      label: 'Type',
      align: 'center',
    },
    {
      id: 'account_info',
      numeric: false,
      label: 'Account',
      align: 'left',
    },
    {
      id: 'external_transfer_method',
      numeric: false,
      label: 'Method',
      align: 'center',
    },
    {
      id: 'expected_transfer_amount',
      numeric: true,
      label: 'Amount (Rounded)',
      sortable: true,
      align: 'center',
    },
    {
      id: 'status',
      numeric: false,
      label: 'Status',
      align: 'center',
    },
  ];

  const dataRoyaltyTransfers = useMemo(
    () => (royaltyTransfers?.results || []).map(row => createRowData(row)),
    [royaltyTransfers]
  );

  // Table selection hook
  const {
    selectedIds,
    selectedItems,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
  } = useTableSelection(dataRoyaltyTransfers, 'id');

  // Listeners
  //re-arranging deck chairs
  const onUnwindClick = async () => {
    if (window.confirm(`Are you sure you want to unwind ID #${editingId}?`)) {
      unwindRoyaltyMutation.mutate({ id: editingId });
    }
  };

  const onCloseClick = async () => {
    setIsClosing(() => {
      return [...isClosing, editingId];
    });
    closeRoyaltyTransferMutation.mutate({ id: editingId });
  };

  const onRefreshAmountClick = useCallback(
    () => executeRefreshMutation.mutate({ id: editingId }),
    [editingId, executeRefreshMutation]
  );
  const onTransferClick = useCallback(
    () => executeExternalTransferMutation.mutate({ id: editingId }),
    [editingId, executeExternalTransferMutation]
  );

  const onToggleBlockBulkOperations = useCallback(() => {
    const objectToPatch = royaltyTransfers.results.find(x => x.royalty_transfer_id === editingId);
    if (!objectToPatch) {
      return;
    }

    executePatchMutation.mutate({
      id: editingId,
      patchObject: { block_bulk_operations: !objectToPatch.block_bulk_operations },
    });
  }, [editingId, royaltyTransfers, executePatchMutation]);

  const onExportClick = async id => await exportTransferCsv(id);
  const handleEditRow = id => setEditingId(id);

  // Bulk operations mutations
  const bulkCloseMutation = useMutation(
    async selectedItems => {
      const itemsToClose = selectedItems.filter(
        item => item.can_close && !item.is_closed && !item.block_bulk_operations
      );
      const results = [];

      for (const item of itemsToClose) {
        try {
          const result = await closeRoyaltyTransfer(item.id);
          results.push({ id: item.id, success: true, result });
        } catch (error) {
          results.push({ id: item.id, success: false, error });
        }
      }

      return results;
    },
    {
      onSuccess: results => {
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        if (successful > 0) {
          showToast(
            `Successfully initiated closing of ${successful} transfers${failed > 0 ? `. ${failed} failed.` : ''}`
          );

          // Add successful IDs to isClosing state for visual feedback
          const successfulIds = results.filter(r => r.success).map(r => r.id);
          setIsClosing(prev => [...prev, ...successfulIds]);
        }

        if (failed > 0 && successful === 0) {
          showToast(`Failed to close ${failed} transfers`, 'error');
        }

        clearSelection();
        refreshPageQueries();
      },
      onError: error => {
        showToast(`Error in bulk close operation: ${error?.data?.detail || error.message}`, 'error');
      },
    }
  );

  const bulkExecuteMutation = useMutation(
    async selectedItems => {
      const itemsToExecute = selectedItems.filter(
        item => item.can_execute_external_transfer && !item.external_transfer_id && !item.block_bulk_operations
      );
      const results = [];

      for (const item of itemsToExecute) {
        try {
          const result = await executeExternalTransfer(item.id);
          results.push({ id: item.id, success: true, result });
        } catch (error) {
          results.push({ id: item.id, success: false, error });
        }
      }

      return results;
    },
    {
      onSuccess: results => {
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        if (successful > 0) {
          showToast(
            `Successfully initiated execution of ${successful} transfers${failed > 0 ? `. ${failed} failed.` : ''}`
          );
        }

        if (failed > 0 && successful === 0) {
          showToast(`Failed to execute ${failed} transfers`, 'error');
        }

        clearSelection();
        refreshPageQueries();
      },
      onError: error => {
        showToast(`Error in bulk execute operation: ${error?.data?.detail || error.message}`, 'error');
      },
    }
  );

  const bulkExportMutation = useMutation(
    async selectedItems => {
      const results = [];

      for (const item of selectedItems) {
        try {
          const result = await exportTransferCsv(item.id);
          results.push({ id: item.id, success: true, result });
        } catch (error) {
          results.push({ id: item.id, success: false, error });
        }
      }

      return results;
    },
    {
      onSuccess: results => {
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        if (successful > 0) {
          showToast(`Successfully exported ${successful} transfers${failed > 0 ? `. ${failed} failed.` : ''}`);
        }

        if (failed > 0 && successful === 0) {
          showToast(`Failed to export ${failed} transfers`, 'error');
        }

        clearSelection();
      },
      onError: error => {
        showToast(`Error in bulk export operation: ${error?.data?.detail || error.message}`, 'error');
      },
    }
  );

  // Bulk unwind mutation
  const bulkUnwindMutation = useMutation(
    async selectedItems => {
      const itemsToUnwind = selectedItems.filter(
        item =>
          !item.is_closed &&
          !item.block_bulk_operations &&
          (item.expected_transfer_amount === null || item.expected_transfer_amount <= 0)
      );
      const results = [];

      for (const item of itemsToUnwind) {
        try {
          const result = await unwindRoyalyTransfers(item.id);
          results.push({ id: item.id, success: true, result });
        } catch (error) {
          results.push({ id: item.id, success: false, error });
        }
      }

      return results;
    },
    {
      onSuccess: results => {
        const successful = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        if (successful > 0) {
          showToast(`Successfully unwound ${successful} transfers${failed > 0 ? `. ${failed} failed.` : ''}`);
        }

        if (failed > 0 && successful === 0) {
          showToast(`Failed to unwind ${failed} transfers`, 'error');
        }

        clearSelection();
        refreshPageQueries();
      },
      onError: error => {
        showToast(`Error in bulk unwind operation: ${error?.data?.detail || error.message}`, 'error');
      },
    }
  );

  // Bulk operations handlers
  const handleBulkClose = useCallback(() => {
    bulkCloseMutation.mutate(selectedItems);
  }, [selectedItems, bulkCloseMutation]);

  const handleBulkExecute = useCallback(() => {
    bulkExecuteMutation.mutate(selectedItems);
  }, [selectedItems, bulkExecuteMutation]);

  const handleBulkUnwind = useCallback(() => {
    bulkUnwindMutation.mutate(selectedItems);
  }, [selectedItems, bulkUnwindMutation]);

  const handleBulkExport = useCallback(() => {
    bulkExportMutation.mutate(selectedItems);
  }, [selectedItems, bulkExportMutation]);

  const handleRequestSort = property => {
    setParams(currentParams => {
      const orderDirection = currentParams.orderBy === property && currentParams.order === 'desc' ? 'asc' : 'desc';
      return { ...currentParams, orderBy: property, order: orderDirection };
    });
  };

  // Helper function to get filter config for a column
  const getColumnFilterConfig = columnId => {
    return updatedColumnFiltersConfig.find(config => config.id === columnId);
  };

  // Filter handlers
  const handleOpenFilterPopup = columnId => {
    openFilterPopup(columnId, {});
  };

  const handleFilterValueChange = (key, value) => {
    updateTempFilter(key, value);
  };

  const handleApplyFilter = () => {
    applyFilter();
  };

  const handleClearFilter = () => {
    clearCurrentFilter();
  };

  const handleRemoveFilter = filterId => {
    removeFilter(filterId);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
  };

  if (isFetchingRoyaltyTransfers)
    return (
      <MuiBox sx={{ width: '100%', overflow: 'hidden' }}>
        <div className={classes.loadWrapper}>
          <MuiCircularProgress />
        </div>
      </MuiBox>
    );

  return (
    <MuiBox sx={{ width: '100%', overflow: 'hidden' }}>
      <MuiTableContainer
        component={MuiPaper}
        style={{
          overflow: 'hidden',
          overflowX: 'auto',
          maxWidth: '100%',
        }}>
        {/* Active Filters */}
        <ActiveFilters
          filters={filters}
          columns={updatedColumnFiltersConfig}
          onClearFilter={handleRemoveFilter}
          onClearAllFilters={handleClearAllFilters}
        />

        {/* Bulk Actions */}
        <BulkActionsRoyaltyTransfers
          selectedCount={selectedIds.length}
          selectedItems={selectedItems}
          onClearSelection={clearSelection}
          onBulkClose={handleBulkClose}
          onBulkExecute={handleBulkExecute}
          onBulkUnwind={handleBulkUnwind}
          onBulkExport={handleBulkExport}
          loading={
            bulkCloseMutation.isLoading ||
            bulkExecuteMutation.isLoading ||
            bulkUnwindMutation.isLoading ||
            bulkExportMutation.isLoading
          }
        />

        <MuiTable
          aria-label="collapsible table"
          size="small"
          style={{
            width: '100%',
            tableLayout: 'fixed',
          }}>
          <colgroup>
            <col style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }} /> {/* Checkbox */}
            <col style={{ width: '90px', minWidth: '90px', maxWidth: '90px' }} /> {/* ID */}
            <col style={{ width: '90px', minWidth: '90px', maxWidth: '90px' }} /> {/* Type */}
            <col style={{ width: '200px', minWidth: '180px', maxWidth: '220px' }} /> {/* Account */}
            <col style={{ width: '90px', minWidth: '90px', maxWidth: '90px' }} /> {/* Method */}
            <col style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }} /> {/* Amount */}
            <col style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }} /> {/* Status */}
            <col style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }} /> {/* Actions */}
          </colgroup>
          <MuiTableHead>
            {/* Paginação Superior */}
            <MuiTableRow>
              <MuiTableCell colSpan={headCells.length + 1} align="right">
                <MuiBox display="flex" justifyContent="flex-end">
                  <MuiTablePagination
                    rowsPerPageOptions={[250, 500, 1000]}
                    rowsPerPage={params.rowsPerPage}
                    count={royaltyTransfers?.total || 0}
                    page={params.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </MuiBox>
              </MuiTableCell>
            </MuiTableRow>
            <MuiTableRow>
              {headCells.map(headCell => {
                if (headCell.id === 'select') {
                  return (
                    <MuiTableCell key={headCell.id} padding="checkbox">
                      <MuiCheckbox
                        indeterminate={isSomeSelected}
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        inputProps={{ 'aria-label': 'select all transfers' }}
                        disabled={dataRoyaltyTransfers.length === 0}
                      />
                    </MuiTableCell>
                  );
                }

                const filterConfig = getColumnFilterConfig(headCell.id);
                return (
                  <MuiTableCell
                    key={headCell.id}
                    align={headCell.align || (headCell.numeric ? 'right' : 'left')}
                    sortDirection={params.orderBy === headCell.id ? params.order : false}>
                    <MuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent={
                        headCell.align === 'center' ? 'center' : headCell.align === 'right' ? 'flex-end' : 'flex-start'
                      }>
                      {headCell.sortable ? (
                        <MuiTableSortLabel
                          active={params.orderBy === headCell.id}
                          direction={params.orderBy === headCell.id ? params.order : 'asc'}
                          onClick={_ => handleRequestSort(headCell.id)}>
                          {headCell.label}
                        </MuiTableSortLabel>
                      ) : (
                        <>{headCell.label}</>
                      )}

                      {/* Column Filter */}
                      {filterConfig && (
                        <ColumnFilter
                          columnId={headCell.id}
                          columnLabel={headCell.label}
                          filterType={filterConfig.filterType}
                          hasFilter={hasColumnFilter(headCell.id)}
                          onOpenFilter={handleOpenFilterPopup}
                          onApplyFilter={handleApplyFilter}
                          onClearFilter={handleClearFilter}
                          filterValues={openFilterColumn?.id === headCell.id ? tempFilters : {}}
                          onFilterValueChange={handleFilterValueChange}
                          options={filterConfig.options}
                        />
                      )}
                    </MuiBox>
                  </MuiTableCell>
                );
              })}
              <MuiTableCell></MuiTableCell>
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {dataRoyaltyTransfers.map(row => (
              <Row
                key={row.id}
                row={row}
                editing={editingId === row.id}
                onEditChange={handleEditRow}
                onTransferClick={onTransferClick}
                onCloseClick={onCloseClick}
                onUnwindClick={onUnwindClick}
                onRefreshAmountClick={onRefreshAmountClick}
                isLoadingUnwind={unwindRoyaltyMutation.isLoading}
                onToggleBlockBulkOperations={onToggleBlockBulkOperations}
                onExportClick={onExportClick}
                onEdit={onEditRow}
                isClosing={isClosing.includes(row.id)}
                isSelected={isSelected(row.id)}
                onToggleSelection={() => toggleSelection(row.id)}
              />
            ))}
          </MuiTableBody>
          {/* Paginação Inferior */}
          <MuiTableFooter>
            <MuiTableRow>
              <MuiTableCell colSpan={headCells.length + 1} align="right">
                <MuiBox display="flex" justifyContent="flex-end">
                  <MuiTablePagination
                    rowsPerPageOptions={[250, 500, 1000]}
                    rowsPerPage={params.rowsPerPage}
                    count={royaltyTransfers?.total || 0}
                    page={params.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </MuiBox>
              </MuiTableCell>
            </MuiTableRow>
          </MuiTableFooter>
        </MuiTable>
      </MuiTableContainer>
      <AlertToast message={toastMessage} onClear={clearToast} alertType={toastType} />
    </MuiBox>
  );
};

export default withStyles(styles)(withRouter(TableRoyaltyTransfers));
